import type { PropsWithChildren } from 'react';

import { Badge } from 'antd';

import { constNull, fn, nat, pipe, remote } from '@code-expert/prelude';
import { useDeepCompareMemo } from '@code-expert/react';
import type { CourseId, SemesterId } from '/imports/domain';
import { GuardRemote } from '/imports/ui/components/GuardRemote';
import { useCourseInboxCount } from '/imports/ui/components/Messages/hooks';
import { useUserInboxContext } from '/imports/ui/components/Messages/UserInboxContext';
import { ThemeProvider } from '/imports/ui/foundation/Theme';

type MessageInboxCountProps = PropsWithChildren<{
  offset?: [number, number];
}>;

const MessageInboxCount = ({
  count,
  offset,
  children,
}: MessageInboxCountProps & { count: remote.Remote<nat.Nat> }) => (
  <ThemeProvider
    themeOverride={({ tokens }) => ({
      components: {
        Badge: {
          colorBgContainer: tokens.colorWhite,
          colorError: tokens.colorPrimary,
          colorErrorHover: tokens.colorPrimaryHover,
        },
      },
    })}
  >
    <GuardRemote
      value={count}
      pending={constNull}
      render={(count) => (
        <Badge count={count} offset={offset}>
          {children}
        </Badge>
      )}
    />
  </ThemeProvider>
);

export const UserInboxCount = ({ offset, children }: MessageInboxCountProps) => {
  const { userMessages, userNotifications } = useUserInboxContext();

  const total = useDeepCompareMemo(
    () =>
      pipe(
        remote.sequenceT(userMessages.count, userNotifications.count),
        remote.map(fn.tupled(nat.SumMonoid.concat)),
      ),
    [userMessages.count, userNotifications.count],
  );

  return (
    <MessageInboxCount count={total} offset={offset}>
      {children}
    </MessageInboxCount>
  );
};

export const CourseInboxCount = ({
  courseId,
  semester,
  offset,
  children,
}: MessageInboxCountProps & { courseId: CourseId; semester: SemesterId }) => {
  const { count } = useCourseInboxCount({ courseId, semester });
  return (
    <MessageInboxCount count={count} offset={offset}>
      {children}
    </MessageInboxCount>
  );
};
